import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { usePageTitle } from "@/i18n/LanguageContext";
import { pages } from "../content/pages";
import { useContent, SlotImg, KMarkV2, Rich, prefersReducedMotion } from "../ui/helpers";
import { PageShell, PageHero, CtaSignOff, StatusChip } from "../ui/blocks";

/* ---------- KS-101 + K SMART V2 ---------- */
function Products({ c }) {
  return (
    <section className="navy-1 sec" id="products" aria-labelledby="products-title">
      <div className="k-wrap">
        <div className="sec-head rv">
          <div><p className="k-label">{c.eyebrow}</p><h2 id="products-title" className="h-sec" style={{ marginTop: 16 }}>{c.title}</h2></div>
          <p>{c.intro}</p>
        </div>
        <div className="prod-pair">
          {c.items.map((p, i) => (
            <article key={p.name} className="prod-item rv" style={{ transitionDelay: `${i * 0.12}s` }}>
              <p className="k-label">{p.kind}</p>
              <h3>{p.name}</h3>
              <p><Rich text={p.body} /></p>
              <ul className="ticks">{p.points.map((x) => <li key={x}>{x}</li>)}</ul>
            </article>
          ))}
          <svg className="prod-link" viewBox="0 0 80 20" aria-hidden="true"><path d="M0 10 H80" /></svg>
        </div>
        <p className="prod-foot rv"><span>{c.specs}</span> <Link className="tlink" to="/demo#platform-demo">{c.demoLink} →</Link></p>
      </div>
    </section>
  );
}

/* ---------- Four modules (tabs + detail panel, arrow-key navigation) ---------- */
function Modules({ c }) {
  const [i, setI] = useState(0);
  const tabs = useRef([]);
  const m = c.items[i];
  const onKey = (e, k) => {
    const n = c.items.length;
    const next = ["ArrowDown", "ArrowRight"].includes(e.key) ? (k + 1) % n : ["ArrowUp", "ArrowLeft"].includes(e.key) ? (k - 1 + n) % n : null;
    if (next === null) return;
    e.preventDefault(); setI(next); tabs.current[next]?.focus();
  };
  return (
    <section className="band-light sec" id="modules" aria-labelledby="modules-title">
      <div className="k-wrap">
        <div className="sec-head rv"><h2 id="modules-title" className="h-sec">{c.title}</h2><p>{c.intro}</p></div>
        <div className="mods">
          <ul className="mod-tabs" role="tablist" aria-label={c.title}>
            {c.items.map((it, k) => (
              <li key={it.num} role="presentation">
                <button type="button" role="tab" id={`mod-tab-${k}`} aria-selected={k === i} aria-controls="mod-panel" tabIndex={k === i ? 0 : -1}
                  ref={(el) => { tabs.current[k] = el; }} className={`mod-tab${k === i ? " on" : ""}`} onClick={() => setI(k)} onKeyDown={(e) => onKey(e, k)}>
                  <i>{it.num}</i><span><strong>{it.name}</strong><small><StatusChip status={it.status} /></small></span>
                </button>
              </li>
            ))}
          </ul>
          <div className="mod-panel" key={i} role="tabpanel" id="mod-panel" aria-labelledby={`mod-tab-${i}`}>
            <div className="mod-img"><SlotImg slot={m.media} sizes="(min-width: 900px) 40vw, 100vw" /></div>
            <div className="mod-body">
              <StatusChip status={m.status} />
              <h3>{m.headline}</h3>
              <p><Rich text={m.body} /></p>
              <ul className="ticks">{m.features.map((f) => <li key={f}>{f}</li>)}</ul>
              <div className="example"><b>{c.exampleLabel}</b><Rich text={m.example} /></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Demo frame (rotates between three role screens) ---------- */
function DemoFrame({ c }) {
  const [i, setI] = useState(0);
  const [auto, setAuto] = useState(!prefersReducedMotion());
  useEffect(() => {
    if (!auto) return undefined;
    const t = setInterval(() => setI((n) => (n + 1) % c.views.length), 5000);
    return () => clearInterval(t);
  }, [auto, c.views.length]);
  const v = c.views[i];
  return (
    <section className="navy sec" id="demo" aria-labelledby="demo-title">
      <div className="k-wrap">
        <div className="sec-head rv"><div><p className="k-label">{c.eyebrow}</p><h2 id="demo-title" className="h-sec" style={{ marginTop: 16 }}>{c.title}</h2></div><p>{c.intro}</p></div>
        <div className="frame rv">
          <div className="frame-bar">
            <div className="frame-dots" aria-hidden="true"><i /><i /><i /></div>
            <div className="frame-tabs" role="tablist" aria-label={c.title}>
              {c.views.map((x, k) => (
                <button key={x.role} type="button" role="tab" aria-selected={k === i} onClick={() => { setAuto(false); setI(k); }}>{x.role}</button>
              ))}
            </div>
            <span className="frame-note">{c.note}</span>
          </div>
          <div className="frame-view" key={i} role="tabpanel">
            <div className="frame-shot"><SlotImg slot={v.media} sizes="(min-width: 900px) 60vw, 100vw" /></div>
            <div className="frame-side">
              <span className="k-label">{v.tag}</span>
              <h3>{v.role}</h3>
              <p>{v.text}</p>
              <ul>{v.stats.map(([k, val]) => <li key={k}>{k}<span>{val}</span></li>)}</ul>
              <Link className="k-btn k-btn-mint k-btn-sm" style={{ marginTop: "auto", alignSelf: "flex-start" }} to="/demo">{c.open} <span className="ar" aria-hidden="true">→</span></Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Integration hub ---------- */
function Integrate({ c }) {
  const item = ([icon, title, sub]) => <li key={title}><i aria-hidden="true">{icon}</i><span>{title}<small>{sub}</small></span></li>;
  return (
    <section className="mint-2 sec" id="integrate" aria-labelledby="integrate-title">
      <div className="k-wrap">
        <div className="sec-head rv"><h2 id="integrate-title" className="h-sec">{c.title}</h2><p>{c.intro}</p></div>
        <div className="hub">
          <ul className="hub-col in" aria-label={c.inLabel}>{c.inputs.map(item)}</ul>
          <div className="hub-core"><div><KMarkV2 size={46} pulse /><b>KSS</b><small>{c.core}</small></div></div>
          <ul className="hub-col k-out" aria-label={c.outLabel}>{c.outputs.map(item)}</ul>
        </div>
      </div>
    </section>
  );
}

/* ---------- Deployment timeline (fills as you scroll) ---------- */
function Deploy({ c }) {
  const ref = useRef(null);
  const [prog, setProg] = useState(0);
  const [lit, setLit] = useState(-1);
  useEffect(() => {
    const onScroll = () => {
      const el = ref.current; if (!el) return;
      const r = el.getBoundingClientRect(), mid = window.innerHeight * 0.6;
      setProg(Math.max(0, Math.min(1, (mid - r.top) / r.height)));
      const steps = [...el.querySelectorAll(".dstep")];
      setLit(steps.filter((s) => s.getBoundingClientRect().top < mid).length - 1);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <section className="band-light sec" id="deploy" aria-labelledby="deploy-title">
      <div className="k-wrap">
        <div className="sec-head rv"><h2 id="deploy-title" className="h-sec">{c.title}</h2><p>{c.intro}</p></div>
        <ol className="dline" ref={ref} style={{ "--prog": `${prog * 100}%` }}>
          <span className="fill" aria-hidden="true" />
          {c.steps.map((s, k) => (
            <li key={s.title} className={`dstep${k <= lit ? " lit" : ""}`}>
              <span className="dnum" aria-hidden="true">{k + 1}</span>
              <div><h3>{s.title}</h3><p className="sub">{s.sub}</p></div>
              <div>
                <p className="what"><Rich text={s.body} /></p>
                <div className="deliv"><b>{c.deliverable}</b><strong>{s.out}</strong><span>{s.outBody}</span></div>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

export default function PlatformPage() {
  usePageTitle("platform");
  const c = useContent(pages).platform;
  return (
    <PageShell testId="platform-page">
      <PageHero hero={c.hero} />
      <Products c={c.products} />
      <Modules c={c.modules} />
      <DemoFrame c={c.demo} />
      <Integrate c={c.integrate} />
      <Deploy c={c.deploy} />
      <CtaSignOff cta={c.cta} />
    </PageShell>
  );
}
